<script setup>
import { ref, computed, nextTick } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

import { useDealsStore } from 'dashboard/stores/deals';
import { useQuotesStore } from 'dashboard/stores/quotes';
import { useCompaniesStore } from 'dashboard/stores/companies';
import { useStore } from 'dashboard/composables/store';

const emit = defineEmits(['create']);

const { t } = useI18n();

const quotesStore = useQuotesStore();
const dealsStore = useDealsStore();
const companiesStore = useCompaniesStore();
const globalStore = useStore();

const dialogRef = ref(null);

const form = ref({
  dealId: '',
  companyId: '',
  contactId: '',
  userId: '',
  title: '',
  description: '',
  quoteNumber: '',
  currency: 'USD',
  validUntil: '',
});

const lineItems = ref([
  { name: '', quantity: 1, unitPrice: 0, description: '' }
]);

const rules = {
  dealId: { required },
  title: { required },
};

const v$ = useVuelidate(rules, form);

const isSubmitting = computed(() => quotesStore.getUIFlags.creating);

const currencyOptions = [
  { value: 'USD', label: '$ USD' },
  { value: 'EUR', label: '€ EUR' },
  { value: 'GBP', label: '£ GBP' },
  { value: 'JPY', label: '¥ JPY' },
  { value: 'CAD', label: 'C$ CAD' },
  { value: 'AUD', label: 'A$ AUD' },
  { value: 'NGN', label: '₦ NGN' },
];

const dealOptions = computed(() => {
  return dealsStore.getDealsList.map(deal => ({
    value: deal.id,
    label: `${deal.title} (${deal.value ? new Intl.NumberFormat('en-US', { style: 'currency', currency: deal.currency || 'USD', minimumFractionDigits: 0 }).format(deal.value) : '-'})`,
    deal: deal,
  }));
});

const companyOptions = computed(() => {
  return companiesStore.getCompaniesList.map(company => ({
    value: company.id,
    label: company.name || t('QUOTES.UNTITLED'),
  }));
});

const contactOptions = computed(() => {
  const contacts = globalStore.getters['contacts/getContactsList'] || [];
  return contacts.map(contact => ({
    value: contact.id,
    label: contact.name || contact.email || t('QUOTES.UNTITLED'),
  }));
});

const agentOptions = computed(() => {
  const agents = globalStore.getters['agents/getAgents'] || [];
  return agents.map(agent => ({
    value: agent.id,
    label: agent.name || agent.email || t('QUOTES.UNTITLED'),
  }));
});

const subtotal = computed(() => {
  return lineItems.value.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
});

const taxAmount = computed(() => subtotal.value * 0.1);
const totalAmount = computed(() => subtotal.value + taxAmount.value);

const formatMoney = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: form.value.currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

const addLineItem = () => {
  lineItems.value.push({ name: '', quantity: 1, unitPrice: 0, description: '' });
};

const removeLineItem = (index) => {
  if (lineItems.value.length > 1) {
    lineItems.value.splice(index, 1);
  }
};

const handleDealSelect = (value) => {
  const selected = dealOptions.value.find(opt => opt.value === value);
  if (selected?.deal) {
    form.value.companyId = selected.deal.company?.id || '';
    form.value.contactId = selected.deal.contact?.id || '';
    form.value.userId = selected.deal.user?.id || '';
    form.value.currency = selected.deal.currency || 'USD';
  }
};

const handleDealSearch = (query) => {
  if (query.length >= 2) {
    dealsStore.search({ search: query });
  }
};

const handleCompanySearch = (query) => {
  if (query.length >= 2) {
    companiesStore.search({ search: query });
  }
};

const handleContactSearch = (query) => {
  if (query.length >= 2) {
    globalStore.dispatch('contacts/search', { search: query });
  }
};

const resetForm = () => {
  form.value = {
    dealId: '',
    companyId: '',
    contactId: '',
    userId: '',
    title: '',
    description: '',
    quoteNumber: '',
    currency: 'USD',
    validUntil: '',
  };
  lineItems.value = [{ name: '', quantity: 1, unitPrice: 0, description: '' }];
  v$.value.$reset();
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  const validLineItems = lineItems.value
    .filter(item => item.name.trim() && item.quantity > 0)
    .map(item => ({
      name: item.name,
      description: item.description,
      quantity: parseInt(item.quantity),
      unit_price: parseFloat(item.unitPrice),
    }));

  const quoteData = {
    deal_id: form.value.dealId || null,
    company_id: form.value.companyId || null,
    contact_id: form.value.contactId || null,
    user_id: form.value.userId || null,
    title: form.value.title,
    description: form.value.description || null,
    quote_number: form.value.quoteNumber || null,
    currency: form.value.currency,
    valid_until: form.value.validUntil || null,
    line_items: validLineItems,
  };

  try {
    await quotesStore.create(quoteData);
    emit('create', quoteData);
    dialogRef.value?.close();
    resetForm();
  } catch (error) {
    // Error handled in store
  }
};

const handleClose = () => {
  resetForm();
};

const open = () => {
  dialogRef.value?.open();
  dealsStore.get({ page: 1 });
  companiesStore.get({ page: 1 });
  globalStore.dispatch('contacts/get', { page: 1 });
  globalStore.dispatch('agents/get');
  nextTick(() => {
    document.querySelector('[data-deal-select] input')?.focus();
  });
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('QUOTES.CREATE.TITLE')"
    width="3xl"
    :overflow-y-auto="true"
    @close="handleClose"
  >
    <div class="py-4 space-y-6">
      <!-- Section 1: Deal & Basic Info -->
      <section class="space-y-4">
        <h3 class="text-sm font-semibold text-n-slate-12 uppercase tracking-wide">
          {{ $t('QUOTES.FORM.DEAL.LABEL') }}
        </h3>
        
        <div data-deal-select>
          <ComboBox
            v-model="form.dealId"
            :options="dealOptions"
            :placeholder="$t('QUOTES.FORM.DEAL.PLACEHOLDER')"
            use-api-results
            @search="handleDealSearch"
            @update:model-value="handleDealSelect"
          />
          <span v-if="v$.dealId.$error" class="text-xs text-n-ruby-11 mt-1 block">
            {{ $t('QUOTES.FORM.DEAL.REQUIRED') }}
          </span>
        </div>

        <div class="grid grid-cols-12 gap-4">
          <div class="col-span-8">
            <Input
              v-model="form.title"
              :label="$t('QUOTES.FORM.TITLE_LABEL')"
              :placeholder="$t('QUOTES.FORM.TITLE_PLACEHOLDER')"
              :error="v$.title.$error ? $t('QUOTES.FORM.TITLE_REQUIRED') : ''"
            />
          </div>
          <div class="col-span-4">
            <Input
              v-model="form.quoteNumber"
              :label="$t('QUOTES.FORM.QUOTE_NUMBER.LABEL')"
              :placeholder="$t('QUOTES.FORM.QUOTE_NUMBER.PLACEHOLDER')"
            />
          </div>
        </div>

        <TextArea
          v-model="form.description"
          :label="$t('QUOTES.FORM.DESCRIPTION.LABEL')"
          :placeholder="$t('QUOTES.FORM.DESCRIPTION.PLACEHOLDER')"
          rows="2"
        />
      </section>

      <!-- Section 2: {{ $t('QUOTES.FORM.SETTINGS') }} -->
      <section class="space-y-4 pt-4 border-t border-n-slate-6">
        <h3 class="text-sm font-semibold text-n-slate-12 uppercase tracking-wide">
          Settings
        </h3>
        
        <div class="grid grid-cols-3 gap-4">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ $t('QUOTES.FORM.CURRENCY.LABEL') }}
            </label>
            <ComboBox v-model="form.currency" :options="currencyOptions" />
          </div>
          <div>
            <Input
              v-model="form.validUntil"
              type="date"
              :label="$t('QUOTES.FORM.VALID_UNTIL.LABEL')"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ $t('DEALS.FORM.ASSIGNEE_LABEL') }}
            </label>
            <ComboBox
              v-model="form.userId"
              :options="agentOptions"
              :placeholder="$t('DEALS.FORM.ASSIGNEE_PLACEHOLDER')"
            />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ $t('DEALS.FORM.COMPANY_LABEL') }}
            </label>
            <ComboBox
              v-model="form.companyId"
              :options="companyOptions"
              :placeholder="$t('DEALS.FORM.COMPANY_PLACEHOLDER')"
              use-api-results
              @search="handleCompanySearch"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ $t('DEALS.FORM.CONTACT_LABEL') }}
            </label>
            <ComboBox
              v-model="form.contactId"
              :options="contactOptions"
              :placeholder="$t('DEALS.FORM.CONTACT_PLACEHOLDER')"
              use-api-results
              @search="handleContactSearch"
            />
          </div>
        </div>
      </section>

      <!-- Section 3: Line Items -->
      <section class="space-y-4 pt-4 border-t border-n-slate-6">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold text-n-slate-12 uppercase tracking-wide">
            {{ $t('QUOTES.FORM.LINE_ITEMS.LABEL') }}
          </h3>
          <Button
            :label="$t('QUOTES.FORM.LINE_ITEMS.ADD')"
            icon="i-lucide-plus"
            size="sm"
            variant="secondary"
            @click="addLineItem"
          />
        </div>

        <!-- Line Items Table -->
        <div class="border border-n-slate-6 rounded-lg overflow-hidden bg-n-alpha-1">
          <!-- Table Header -->
          <div class="grid grid-cols-12 gap-3 px-4 py-2.5 bg-n-slate-4 text-xs font-medium text-n-slate-11 border-b border-n-slate-6">
            <div class="col-span-5">{{ $t('QUOTES.FORM.LINE_ITEMS.NAME_PLACEHOLDER') }}</div>
            <div class="col-span-2 text-center">{{ $t('QUOTES.FORM.LINE_ITEMS.QUANTITY') }}</div>
            <div class="col-span-2 text-right">{{ $t('QUOTES.FORM.LINE_ITEMS.UNIT_PRICE') }}</div>
            <div class="col-span-2 text-right">Total</div>
            <div class="col-span-1"></div>
          </div>

          <!-- Table Body -->
          <div class="divide-y divide-n-slate-6">
            <div v-for="(item, index) in lineItems" :key="index" class="grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-n-slate-3/30">
              <!-- Item Name & Description -->
              <div class="col-span-5 space-y-1.5">
                <Input
                  v-model="item.name"
                  placeholder="Item name"
                  class="text-sm"
                />
                <Input
                  v-model="item.description"
                  placeholder="Description (optional)"
                  class="text-xs text-n-slate-11"
                />
              </div>
              
              <!-- Quantity -->
              <div class="col-span-2">
                <Input
                  v-model.number="item.quantity"
                  type="number"
                  min="1"
                  class="text-center text-sm"
                />
              </div>
              
              <!-- Unit Price -->
              <div class="col-span-2">
                <Input
                  v-model.number="item.unitPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  class="text-right text-sm"
                />
              </div>
              
              <!-- Line Total -->
              <div class="col-span-2 text-right text-sm font-medium text-n-slate-12">
                {{ formatMoney(item.quantity * item.unitPrice) }}
              </div>
              
              <!-- Remove Button -->
              <div class="col-span-1 text-right">
                <button
                  v-if="lineItems.length > 1"
                  class="p-1.5 text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-ruby-9/10 rounded-md transition-colors"
                  @click="removeLineItem(index)"
                >
                  <Icon icon="i-lucide-trash-2" size="size-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Section 4: Summary -->
      <section class="pt-4 border-t border-n-slate-6">
        <div class="flex justify-end">
          <div class="w-80 bg-n-slate-4 rounded-lg p-4 space-y-3">
            <div class="flex justify-between text-sm text-n-slate-11">
              <span>Subtotal</span>
              <span class="font-medium">{{ formatMoney(subtotal) }}</span>
            </div>
            <div class="flex justify-between text-sm text-n-slate-11">
              <span>{{ $t('QUOTES.FORM.TAX') }}</span>
              <span class="font-medium">{{ formatMoney(taxAmount) }}</span>
            </div>
            <div class="pt-2 border-t border-n-slate-6 flex justify-between font-semibold text-n-slate-12">
              <span>{{ $t('QUOTES.FORM.TOTAL') }}</span>
              <span class="text-lg">{{ formatMoney(totalAmount) }}</span>
            </div>
          </div>
        </div>
      </section>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <Button
          :label="$t('QUOTES.CANCEL')"
          variant="ghost"
          @click="dialogRef?.close()"
        />
        <Button
          :label="$t('QUOTES.CREATE.SUBMIT')"
          :loading="isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </template>
  </Dialog>
</template>
